//////////////////////////////////////////////////////////////////////////////
//
//  광고 노출 로그 목록 화면
//
//  작성자: MediInStep Team
//  생성일: 2025-12-21
//
//  설명: 전면 광고 노출 로그 목록 조회, 페이징 기능
//
//////////////////////////////////////////////////////////////////////////////

import { Component } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";
import BasicTable from "ScreenLayouts/TableLayout/BasicTableLayout/BasicTable";

// Network
import { request } from "app/network";
import { useMaterialUIController } from "context";
import { formatLocalDateTime, formatLocalDate, formatLocalTime } from "utils/dateFormatter";

interface AdViewLogListState {
    logs: any[];
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    loading: boolean;
    searchTerm: string;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_ad_view_log_list_page_size";

class AdViewLogListScreenClass extends Component<any, AdViewLogListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 10;

        this.state = {
            logs: [],
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            loading: false,
            searchTerm: "",
        };
    }

    componentDidMount() {
        this.loadLogs();
    }

    loadLogs = async () => {
        try {
            this.setState({ loading: true });

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                SearchKeyword: this.state.searchTerm || undefined,
            };

            const res = await request("AdViewLog.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const logs = (pagedList.items || []).map((log: any) => {
                    const rawViewDt = log.viewDt || null;
                    const hasTimezone = rawViewDt && /(Z|[+-]\d{2}:?\d{2})$/.test(rawViewDt);
                    const viewDt = rawViewDt ? (hasTimezone ? rawViewDt : `${rawViewDt}Z`) : null;

                    const viewDurationSec = log.viewDurationMs
                        ? (log.viewDurationMs / 1000).toFixed(1)
                        : "-";

                    return {
                        id: log.seqId,
                        viewDt,
                        adTitle: log.adTitle || "-",
                        userName: log.userName || "비회원",
                        viewDurationSec,
                        screenName: log.screenName || "-",
                        isSkipped: log.isSkipped,
                        isCompleted: log.isCompleted,
                        deviceType: log.deviceType || "-",
                    };
                });

                this.setState({
                    logs,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("광고 노출 로그 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadLogs();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadLogs();
        });
    };

    handleSearch = (searchValue: string) => {
        this.setState({ searchTerm: searchValue, pageNumber: 1 }, () => {
            this.loadLogs();
        });
    };

    render() {
        const { logs, pageSize, loading, totalCount, pageNumber } = this.state;

        const renderDateCell = (value: string | null) => {
            if (!value || value === "-") return "-";

            return (
                <MDBox lineHeight={1.2} textAlign="center">
                    <MDTypography variant="caption" display="block" color="text" fontWeight="medium">
                        {formatLocalDate(value)}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                        {formatLocalTime(value)}
                    </MDTypography>
                </MDBox>
            );
        };

        const columns = [
            {
                Header: "노출일시",
                accessor: "viewDt",
                align: "center",
                width: "14%",
                Cell: ({ value }: any) => renderDateCell(value)
            },
            { Header: "광고명", accessor: "adTitle", align: "left", width: "15%" },
            { Header: "사용자", accessor: "userName", align: "left", width: "10%" },
            { Header: "시청시간(초)", accessor: "viewDurationSec", align: "right", width: "10%" },
            { Header: "화면명", accessor: "screenName", align: "center", width: "12%" },
            {
                Header: "스킵여부",
                accessor: "isSkipped",
                align: "center",
                width: "8%",
                Cell: ({ row }: any) => {
                    const isSkipped = row.original.isSkipped;
                    if (isSkipped === null || isSkipped === undefined) {
                        return <MDTypography variant="caption">-</MDTypography>;
                    }

                    return (
                        <MDBadge
                            badgeContent={isSkipped ? "스킵" : "미스킵"}
                            color={isSkipped ? "warning" : "info"}
                            variant="gradient"
                            size="sm"
                        />
                    );
                }
            },
            {
                Header: "완료여부",
                accessor: "isCompleted",
                align: "center",
                width: "8%",
                Cell: ({ row }: any) => {
                    const isCompleted = row.original.isCompleted;
                    if (isCompleted === null || isCompleted === undefined) {
                        return <MDTypography variant="caption">-</MDTypography>;
                    }

                    return (
                        <MDBadge
                            badgeContent={isCompleted ? "완료" : "미완료"}
                            color={isCompleted ? "success" : "secondary"}
                            variant="gradient"
                            size="sm"
                        />
                    );
                }
            },
            { Header: "디바이스", accessor: "deviceType", align: "center", width: "10%" },
        ];

        const rows = logs;

        const totalPages = Math.ceil(totalCount / pageSize);
        const pageCountArray = Array.from({ length: totalPages }, (_, i) => i);
        const currentPageIndex = pageNumber - 1;
        const pageTenNumber = Math.floor(currentPageIndex / 10) * 10;

        const paginationCriteria = {
            PageNumber: currentPageIndex,
            PageCount: pageCountArray,
            PageTenNumber: pageTenNumber,
            totalCount: totalCount,
        };

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} position="relative">
                    <Card sx={{ "& table": { tableLayout: "fixed", width: "100%" } }}>
                        <MDBox p={3} lineHeight={1}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                <MDBox display={"flex"} alignContent="center">
                                    <Icon fontSize={"medium"}>visibility</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        광고 노출 기록 ( AD VIEW LOGS )
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                            <MDTypography variant="button" color="text" mt={1}>
                                광고 노출 로그 목록 (전체: {totalCount}개)
                            </MDTypography>
                        </MDBox>
                        <BasicTable
                            canSearch={true}
                            onSearch={this.handleSearch}
                            criteria={paginationCriteria}
                            storePageSize={pageSize}
                            goPage={this.handlePageChange}
                            setStorePageSize={this.handlePageSizeChange}
                            table={{ columns, rows }}
                            isSorted={false}
                            entriesPerPage={{
                                defaultValue: pageSize,
                                entries: [5, 10, 15, 20, 25, 30, 40, 50],
                            }}
                            showTotalEntries={true}
                            noEndBorder
                        />
                        {loading && (
                            <MDBox
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor="rgba(0, 0, 0, 0.3)"
                                sx={{ pointerEvents: "none" }}
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        )}
                    </Card>
                </MDBox>
                <Footer />
            </DashboardLayout>
        );
    }
}

function AdViewLogListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    return <AdViewLogListScreenClass {...props} darkMode={darkMode} />;
}

export default AdViewLogListScreen;
